import { MediaAsset, MediaUploadOptions, CloudinaryTransformOptions, OptimizedMediaUrl } from '@/types/media';
import { supabase } from '@/integrations/supabase/client';

// Helper to detect current connection mode
function getCurrentConnectionMode() {
  if (typeof window === 'undefined') return 'balanced';
  
  // Check DOM data attribute set by connection provider
  const mode = document.documentElement.dataset.mode;
  if (mode && ['full', 'balanced', 'lite', 'offline'].includes(mode)) {
    return mode as 'full' | 'balanced' | 'lite' | 'offline';
  }
  
  // Fallback: check if lite class is present (legacy)
  if (document.documentElement.classList.contains('lite')) {
    return 'lite';
  }
  
  return 'balanced';
}

interface CloudinaryUploadResult {
  public_id: string;
  secure_url: string;
  resource_type: string;
  format: string;
  version: number;
  bytes: number;
  width?: number;
  height?: number;
  duration?: number;
}

class CloudinaryService {
  private cloudName: string;
  private uploadPreset: string;

  constructor() {
    // Using your actual Cloudinary cloud name from the dashboard
    this.cloudName = 'yldsocialvideos'; // Your actual cloud name
    this.uploadPreset = 'YldShareVideos'; // Your upload preset name
  }

  private generateFolderPath(options: MediaUploadOptions): string {
    const { userId, postTitle, postDate, contentType, cropType, profileData } = options;
    
    // Create a date string (YYYY-MM-DD format)
    const dateStr = postDate || new Date().toISOString().split('T')[0];
    
    // Sanitize title for folder name (remove special characters, limit length)
    const sanitizedTitle = postTitle 
      ? postTitle
          .toLowerCase()
          .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
          .replace(/\s+/g, '-') // Replace spaces with hyphens
          .substring(0, 50) // Limit length
      : 'untitled';

    // Base user folder
    const userFolder = userId ? `users/${userId}` : 'users/anonymous';
    
    // Different organization based on content type
    switch (contentType) {
      case 'avatar':
        return `${userFolder}/profile/avatar`;
      
      case 'farm-logo':
        return `${userFolder}/profile/farm-logo`;
      
      case 'farm-gallery':
        const yearMonth = dateStr.substring(0, 7); // YYYY-MM
        return `${userFolder}/farm/gallery/${yearMonth}`;
      
      case 'profile-cover':
        return `${userFolder}/profile/cover`;
      
      case 'image':
      case 'video':
      default:
        // For posts: organize by year-month and crop type if available
        const postYearMonth = dateStr.substring(0, 7); // YYYY-MM
        const cropFolder = cropType ? `/${cropType.toLowerCase().replace(/\s+/g, '-')}` : '';
        const folderName = `${dateStr}_${sanitizedTitle}`;
        return `${userFolder}/posts/${postYearMonth}${cropFolder}/${folderName}`;
    }
  }

  async uploadMedia(file: File, options: MediaUploadOptions): Promise<MediaAsset> {
    const folderPath = this.generateFolderPath(options);
    const connectionMode = getCurrentConnectionMode();
    const isLiteMode = connectionMode === 'lite';
    
    // Determine resource type from file
    const isVideo = file.type.startsWith('video/');
    const resourceType = isVideo ? 'video' : 'image';
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', this.uploadPreset);
    formData.append('resource_type', resourceType);
    formData.append('folder', folderPath);
    
    // Apply lite mode transformations during upload for videos
    if (isVideo && isLiteMode) {
      // For lite mode videos: limit to 360p, low bitrate
      const liteTransform = 'h_360,c_limit,br_500k,q_auto:low,f_mp4';
      formData.append('transformation', liteTransform);
    } else if (isVideo) {
      // For full mode videos: allow up to 720p
      const fullTransform = 'h_720,c_limit,br_2000k,q_auto,f_mp4';
      formData.append('transformation', fullTransform);
    }
    
    // Enhanced tags for better organization and searchability
    const tags = [
      'yieldshare',
      options.userId ? `user_${options.userId}` : 'anonymous',
      options.contentType || 'post',
      options.cropType ? `crop_${options.cropType.toLowerCase().replace(/\s+/g, '-')}` : null,
      options.postDate?.substring(0, 7) || new Date().toISOString().substring(0, 7), // YYYY-MM tag
      options.profileData?.username ? `username_${options.profileData.username}` : null,
      options.profileData?.farmName ? `farm_${options.profileData.farmName.toLowerCase().replace(/\s+/g, '-')}` : null,
      resourceType, // 'video' or 'image'
      connectionMode, // 'lite', 'balanced', 'full'
      isVideo && isLiteMode ? 'lite_optimized' : null, // Flag for lite mode videos
    ].filter(Boolean);
    
    formData.append('tags', tags.join(','));

    try {
      const uploadUrl = `https://api.cloudinary.com/v1_1/${this.cloudName}/${resourceType}/upload`;
      const response = await fetch(uploadUrl, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Failed to upload ${resourceType} to Cloudinary`);
      }

      const result = await response.json();
      console.log(`${resourceType} uploaded successfully to folder:`, folderPath, result);
      
      // Create media asset record in database with connection mode metadata
      const mediaAsset: Omit<MediaAsset, 'id' | 'created_at'> = {
        public_id: result.public_id,
        resource_type: result.resource_type as 'image' | 'video' | 'raw',
        format: result.format,
        version: result.version,
        duration: result.duration,
        bytes: result.bytes,
        secure_url: result.secure_url,
        width: result.width,
        height: result.height,
        folder_path: folderPath,
        tags: tags,
        user_id: options.userId,
        post_id: null, // Will be set later when linking to posts
      };

      // Store metadata in database
      const { data: savedAsset, error } = await supabase
        .from('media_assets')
        .insert(mediaAsset)
        .select()
        .single();

      if (error) {
        console.error('Error saving media asset to database:', error);
        throw new Error('Failed to save media metadata');
      }

      return savedAsset as MediaAsset;
    } catch (error) {
      console.error(`Error uploading ${resourceType}:`, error);
      throw new Error(`Failed to upload ${resourceType}. Please try again.`);
    }
  }

  // Optimized URL builders with no API calls at render time
  
  // Build thumbnail URL with connection mode-aware optimizations
  buildThumbUrl(publicId: string): string {
    const connectionMode = getCurrentConnectionMode();
    const transformations = connectionMode === 'lite' 
      ? 'f_auto,q_auto:low,w_320'
      : 'w_150,h_150,c_fill,f_auto,q_auto';
    const version = Date.now();
    return `https://res.cloudinary.com/${this.cloudName}/image/upload/${transformations}/${publicId}?v=${version}&cache=max-age%3D31536000%2Cpublic%2Cimmutable`;
  }

  // Build video poster frame URL with connection mode awareness
  buildVideoPosterUrl(publicId: string): string {
    const connectionMode = getCurrentConnectionMode();
    const transformations = connectionMode === 'lite'
      ? 'so_2,w_320,h_180,c_fill,f_auto,q_auto:low'
      : 'so_2,w_480,h_270,c_fill,f_auto,q_auto';
    const version = Date.now();
    return `https://res.cloudinary.com/${this.cloudName}/video/upload/${transformations}/${publicId}.jpg?v=${version}&cache=max-age%3D31536000%2Cpublic%2Cimmutable`;
  }

  // Build optimized video URL using public delivery model
  buildVideoStreamUrl(publicId: string): string {
    // Public delivery model: always MP4, codec auto, eco quality, 640px width
    const transformations = 'f_mp4,vc_auto,q_auto:eco,w_640';
    const version = Date.now();
    return `https://res.cloudinary.com/${this.cloudName}/video/upload/${transformations}/${publicId}.mp4?v=${version}&cache=max-age%3D31536000%2Cpublic%2Cimmutable`;
  }

  // Build high-quality image URL with connection mode caps
  buildHighQualityImageUrl(publicId: string, maxWidth = 800): string {
    const connectionMode = getCurrentConnectionMode();
    const width = connectionMode === 'lite' ? 480 : Math.min(maxWidth, connectionMode === 'balanced' ? 640 : 1200);
    const quality = connectionMode === 'lite' ? 'q_auto:low' : connectionMode === 'balanced' ? 'q_auto:eco' : 'q_auto';
    const version = Date.now();
    return `https://res.cloudinary.com/${this.cloudName}/image/upload/f_auto,${quality},w_${width},c_scale/${publicId}?v=${version}&cache=max-age%3D31536000%2Cpublic%2Cimmutable`;
  }

  // Build feed-optimized image URL (responsive to connection mode)
  buildFeedImageUrl(publicId: string): string {
    const connectionMode = getCurrentConnectionMode();
    const width = connectionMode === 'lite' ? 320 : 480;
    const quality = connectionMode === 'lite' ? 'q_auto:low' : 'q_auto:eco';
    const transformations = `w_${width},${quality},f_auto,c_scale`;
    const version = Date.now();
    return `https://res.cloudinary.com/${this.cloudName}/image/upload/${transformations}/${publicId}?v=${version}&cache=max-age%3D31536000%2Cpublic%2Cimmutable`;
  }

  // Build mobile-optimized image URL with connection mode awareness
  buildMobileImageUrl(publicId: string): string {
    const connectionMode = getCurrentConnectionMode();
    const transformations = connectionMode === 'lite'
      ? 'f_auto,q_auto:low,w_320'
      : 'f_auto,q_auto:eco,w_480,c_scale';
    const version = Date.now();
    return `https://res.cloudinary.com/${this.cloudName}/image/upload/${transformations}/${publicId}?v=${version}&cache=max-age%3D31536000%2Cpublic%2Cimmutable`;
  }

  // Extract public_id from Cloudinary URL
  extractPublicId(cloudinaryUrl: string): string {
    // Handle both old and new URL formats
    const match = cloudinaryUrl.match(/\/upload\/(?:v\d+\/)?(.+?)(?:\.[^.]+)?$/);
    return match ? match[1] : cloudinaryUrl;
  }
  buildOptimizedUrl(publicId: string, resourceType: 'image' | 'video', transformations: CloudinaryTransformOptions = {}): OptimizedMediaUrl {
    const baseUrl = `https://res.cloudinary.com/${this.cloudName}/${resourceType}/upload`;
    
    const transformParts: string[] = [];
    
    // Dimension transformations
    if (transformations.width || transformations.height) {
      const dimensions = [
        transformations.width ? `w_${transformations.width}` : '',
        transformations.height ? `h_${transformations.height}` : '',
        transformations.crop ? `c_${transformations.crop}` : 'c_fill'
      ].filter(Boolean);
      transformParts.push(dimensions.join(','));
    }
    
    // Quality and format optimization
    if (transformations.quality) {
      transformParts.push(`q_${transformations.quality}`);
    } else {
      // Default quality optimization for mobile
      transformParts.push('q_auto:low');
    }
    
    if (transformations.format) {
      transformParts.push(`f_${transformations.format}`);
    } else {
      // Auto format selection
      transformParts.push('f_auto');
    }
    
    // Video-specific transformations
    if (resourceType === 'video') {
      if (transformations.video_codec) {
        transformParts.push(`vc_${transformations.video_codec}`);
      }
      if (transformations.audio_codec) {
        transformParts.push(`ac_${transformations.audio_codec}`);
      }
      if (transformations.streaming_profile) {
        transformParts.push(`sp_${transformations.streaming_profile}`);
      }
    }
    
    // Effects
    if (transformations.blur) transformParts.push(`e_blur:${transformations.blur}`);
    if (transformations.brightness) transformParts.push(`e_brightness:${transformations.brightness}`);
    if (transformations.contrast) transformParts.push(`e_contrast:${transformations.contrast}`);
    if (transformations.saturation) transformParts.push(`e_saturation:${transformations.saturation}`);
    
    const transformString = transformParts.length > 0 ? transformParts.join('/') + '/' : '';
    const version = Date.now();
    const url = `${baseUrl}/${transformString}${publicId}?v=${version}&cache=max-age%3D31536000%2Cpublic%2Cimmutable`;
    
    return {
      url,
      publicId,
      transformations
    };
  }

  // Get media asset from database
  async getMediaAsset(publicId: string): Promise<MediaAsset | null> {
    const { data, error } = await supabase
      .from('media_assets')
      .select('*')
      .eq('public_id', publicId)
      .single();
    
    if (error) {
      console.error('Error fetching media asset:', error);
      return null;
    }
    
    return data as MediaAsset | null;
  }

  // Link media asset to a post
  async linkMediaToPost(publicId: string, postId: string): Promise<boolean> {
    const { error } = await supabase
      .from('media_assets')
      .update({ post_id: postId })
      .eq('public_id', publicId);
    
    if (error) {
      console.error('Error linking media to post:', error);
      return false;
    }
    
    return true;
  }

  // Get all media assets for a user
  async getUserMediaAssets(userId: string, resourceType?: 'image' | 'video'): Promise<MediaAsset[]> {
    let query = supabase
      .from('media_assets')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
    
    if (resourceType) {
      query = query.eq('resource_type', resourceType);
    }
    
    const { data, error } = await query;
    
    if (error) {
      console.error('Error fetching user media assets:', error);
      return [];
    }
    
    return (data || []) as MediaAsset[];
  }

  // Generate thumbnail URL from video public_id
  generateThumbnail(publicId: string, width = 400, height = 300): string {
    return this.buildOptimizedUrl(publicId, 'video', {
      width,
      height,
      crop: 'fill',
      format: 'jpg'
    }).url;
  }

  // Generate optimized video URL with cache headers
  getOptimizedVideoUrl(publicId: string, quality: CloudinaryTransformOptions['quality'] = 'auto'): string {
    const result = this.buildOptimizedUrl(publicId, 'video', { quality });
    return result.url;
  }

  // Generate responsive image URL with cache headers 
  getResponsiveImageUrl(publicId: string, maxWidth = 800, quality: CloudinaryTransformOptions['quality'] = 'auto'): string {
    const result = this.buildOptimizedUrl(publicId, 'image', {
      width: maxWidth,
      crop: 'scale',
      quality,
      format: 'auto'
    });
    return result.url;
  }

  // Convenience methods for specific upload types
  async uploadVideo(file: File, options: MediaUploadOptions): Promise<MediaAsset> {
    return this.uploadMedia(file, { ...options, contentType: 'video' });
  }

  async uploadImage(file: File, options: MediaUploadOptions): Promise<MediaAsset> {
    return this.uploadMedia(file, { ...options, contentType: 'image' });
  }

  async uploadAvatar(file: File, userId: string, profileData?: MediaUploadOptions['profileData']): Promise<MediaAsset> {
    return this.uploadMedia(file, { 
      userId, 
      contentType: 'avatar',
      profileData 
    });
  }

  async uploadFarmLogo(file: File, userId: string, profileData?: MediaUploadOptions['profileData']): Promise<MediaAsset> {
    return this.uploadMedia(file, { 
      userId, 
      contentType: 'farm-logo',
      profileData 
    });
  }

  async uploadFarmGallery(file: File, userId: string, profileData?: MediaUploadOptions['profileData']): Promise<MediaAsset> {
    return this.uploadMedia(file, { 
      userId, 
      contentType: 'farm-gallery',
      profileData 
    });
  }

  // Method to get folder structure info
  getFolderInfo(userId: string, postDate?: string): string {
    const options: MediaUploadOptions = { userId, postDate };
    return this.generateFolderPath(options);
  }
}

export const cloudinaryService = new CloudinaryService();